import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, CheckCircle2, AlertTriangle, Clock, FileText } from 'lucide-react';
import { useFetch, Spinner } from '../lib/useFetch';
import { registriesApi, companiesApi } from '../lib/api';
import RegistryEntryDialog from '../components/RegistryEntryDialog';
import { fmtDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';

const RegistroDetailPage = () => {
  const { code } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const companyId = isClient ? user?.company : (search.get('company') || '');
  const [newOpen, setNewOpen] = useState(false);

  const { data: templates } = useFetch(() => registriesApi.templates(), []);
  const { data: company } = useFetch(() => companyId ? companiesApi.get(companyId) : Promise.resolve({ data: null }), [companyId]);
  const { data: entries, loading, reload } = useFetch(
    () => companyId ? registriesApi.entries({ companyId, templateCode: code, limit: 200 }) : Promise.resolve({ data: [] }),
    [companyId, code],
  );

  const template = useMemo(() => (templates || []).find((t) => t.code === code), [templates, code]);

  if (loading) return <Spinner />;
  if (!template) return <div className="p-10 text-center text-slate-400">Registro non trovato</div>;

  return (
    <div className="space-y-5" data-testid="registro-detail-page">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <button onClick={() => navigate('/registri')} className="p-1.5 rounded-md hover:bg-gray-100"><ArrowLeft size={16} /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-800">{template.label}</h1>
            <p className="text-xs text-slate-500">
              {company?.name && <>per <strong>{company.name}</strong> · </>}
              <span className="uppercase tracking-wider">{template.macroArea}</span> · {template.frequency} · {(entries || []).length} compilazioni
            </p>
          </div>
          {!isClient && companyId && (
            <button onClick={() => setNewOpen(true)} className="ro-hide px-4 py-2 rounded-full bg-[#023F80] hover:bg-[#1e293b] text-white text-xs font-bold flex items-center gap-2" data-testid="registro-new-entry">
              <Plus size={14} /> NUOVA COMPILAZIONE
            </button>
          )}
        </div>

        <div className="space-y-3 mt-4">
          {(entries || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">Nessuna compilazione registrata</div>
          ) : (
            (entries || []).map((e) => {
              const ok = e.conformity === 'conforme';
              const nc = e.conformity === 'non_conforme';
              return (
                <div key={e.id} className="border border-slate-200 rounded-xl p-4" data-testid={`entry-${e.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${ok ? 'bg-emerald-100 text-emerald-800' : nc ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                        {ok ? <CheckCircle2 size={11} className="inline mr-1" /> : nc ? <AlertTriangle size={11} className="inline mr-1" /> : <Clock size={11} className="inline mr-1" />}
                        {e.conformity?.replace('_', ' ') || '—'}
                      </span>
                      <span className="text-sm font-bold text-slate-800">{fmtDate(e.timestamp?.slice(0, 10))} {(e.timestamp || '').slice(11, 16)}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {e.operator?.name && <>Operatore: <strong>{e.operator.name}</strong> · </>}
                      {e.operator?.signature && <span className="text-emerald-700">✓ firmato</span>}
                    </div>
                  </div>
                  <table className="w-full mt-3 text-xs">
                    <tbody>
                      {Object.entries(e.data || {}).map(([k, v]) => {
                        const f = (template.fields || []).find((x) => x.name === k);
                        if (!f) return null;
                        let display = v;
                        if (Array.isArray(v)) display = v.join(', ');
                        else if (typeof v === 'boolean') display = v ? 'Sì' : 'No';
                        return (
                          <tr key={k} className="border-b border-slate-50">
                            <td className="py-1.5 text-slate-500 w-1/3">{f.label}{f.unit && ` (${f.unit})`}</td>
                            <td className="py-1.5 font-semibold text-slate-800">{display ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {e.note && <p className="text-xs text-slate-600 mt-2 italic">"{e.note}"</p>}
                  {Array.isArray(e.attachments) && e.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {e.attachments.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700">
                          <FileText size={11} /> {a.filename || 'allegato'}
                        </span>
                      ))}
                    </div>
                  )}
                  {e.nonConformityId && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-800">
                      ⚠ Non conformità aperta — gestiscila dalla dashboard Registri.
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <RegistryEntryDialog
        template={template}
        companyId={companyId}
        open={newOpen}
        onOpenChange={setNewOpen}
        onDone={reload}
      />
    </div>
  );
};

export default RegistroDetailPage;
